// Movement modes (decision 0044): locomotion is a set of modes, each with entry and exit rules, run by a small
// machine. Ground, air and glide are built here; climb, swim and mounted are slots: the rules that enter them exist
// and fire only when the game registers a mode with that ID, so they slot in without changing the controller.
//
// A step: timers age; the input rules are checked (the first that holds moves the mode on); the mode steps (it sets
// the velocity and facing and returns the translation to ask the collision world for); the character moves; the mode
// reads the result; the contact rules are checked. At most one change per rule phase, so a mode always runs a step
// before it can hand over again on the same kind of rule.
import type { BodyId, CharacterMove, CollisionWorld, Vec3 } from "@rpg/collision";
import { groundSpeed, type ControllerInput, type ControllerState, type ModeId } from "./state";
import { speedForTilt, type ControllerRates, type ControllerTuning } from "./tuning";
import { forwardOf, turnToward, wrapAngle, yawOf } from "./yaw";

/** Planted defects (tests only): each breaks one thing a check guards. */
export interface ControllerFaults {
  /** A jump pressed after walking off a ledge never jumps: the coyote-time gate must go red. */
  noCoyote: boolean;
}

/** What a mode and a rule see during one step. */
export interface ModeContext {
  readonly s: ControllerState;
  readonly input: ControllerInput;
  readonly t: ControllerTuning;
  readonly r: ControllerRates;
  readonly dt: number;
  readonly world: CollisionWorld;
  readonly body: BodyId;
  readonly faults: ControllerFaults;
  /** Speed × this: the surface underfoot and wading. */
  readonly speedScale: number;
  /** The last move's result: null before the move, and when the mode didn't move the body. */
  readonly move: CharacterMove | null;
  /** The game's climbable surface tags. */
  readonly climbTags: readonly string[];
  /** Whether the controller has a mode with this ID. */
  hasMode(id: ModeId): boolean;
  /** The height of the feet above the ground or the water below, or Infinity past `max` m. */
  heightAbove(max: number): number;
  /** Place the body with no collision (a rider on a seat): the feet go to `feet`. */
  place(feet: Vec3): void;
  /** Set when a take-off spent this step's jump press. */
  jumpSpent: boolean;
}

/** One movement mode. Built: ground, air, glide. Slots: climb, swim, mounted. */
export interface MovementMode {
  readonly id: ModeId;
  /** Runs when the mode takes over from `from`. */
  enter?(c: ModeContext, from: ModeId): void;
  /** Runs when the mode hands over to `to`. */
  exit?(c: ModeContext, to: ModeId): void;
  /** One step: update the velocity and facing, and return the translation to ask the collision world for, or null to
   *  leave the body where the mode put it (with `place`). */
  step(c: ModeContext): Vec3 | null;
  /** After the move (`c.move` holds the result). */
  after?(c: ModeContext): void;
}

/** Climbing a wall whose surface tag is climbable (a slot: no built mode). */
export interface ClimbMode extends MovementMode {
  readonly id: "climb";
}
/** Swimming in water deeper than the tuning's `swimDepth` (a slot). */
export interface SwimMode extends MovementMode {
  readonly id: "swim";
}
/** Riding another controllable (a mount, a boat) after a control handoff sets `riding` (a slot the game fills). */
export interface MountedMode extends MovementMode {
  readonly id: "mounted";
}

/** An entry or exit rule: in phase `when`, from mode `from`, go to mode `to` when `test` holds. */
export interface ModeRule {
  name: string;
  from: ModeId | readonly ModeId[] | "*";
  to: ModeId;
  /** "input": before the step, on the input and the state; "contact": after the move, on what it met. */
  when: "input" | "contact";
  test(c: ModeContext): boolean;
}

/** Take off: the jump's speed up, the press spent. */
export function takeOff(c: ModeContext): void {
  const s = c.s;
  s.vel.y = c.r.jumpSpeed;
  s.jumped = true;
  s.jumpHeld = true;
  s.buffer = -1;
  s.jumpQueued = false;
  s.grounded = false;
  c.jumpSpent = true;
}

const moving = (c: ModeContext): boolean => Math.hypot(c.input.move.x, c.input.move.z) > c.t.deadZone;

/**
 * Ground: body-led free movement (spike 001's controller, its known-good design). The facing turns toward the stick
 * and the velocity runs along the facing, so the feet go where the body points. Three cases each step:
 *   carve  the stick within `skidAngle` of the velocity: turn at the carve speed, keep speed through the turn;
 *   skid   the stick reversed at speed: brake along the old line, facing held (no moonwalk);
 *   pivot  from near rest with the stick far behind: turn in place first, then set off.
 * Moves level (y 0): the collision world's snap keeps it on slopes and down steps.
 */
export function groundMode(): MovementMode {
  return {
    id: "ground",
    enter(c) {
      const s = c.s;
      s.vel.y = 0;
      s.jumped = false;
      s.jumpHeld = false;
      s.sinceGrounded = 0;
      // a jump pressed shortly before landing takes off on the next step
      if (s.buffer >= 0 && s.buffer <= c.t.jumpBuffer + 1e-9) s.jumpQueued = true;
      s.buffer = -1;
    },
    step(c) {
      const { s, t, r, dt, input } = c;
      s.sinceGrounded = 0;
      // a move override (a lunge, a roll): its velocity and facing replace the stick's for this step
      if (input.drive) {
        const d = input.drive;
        s.pivot = false;
        s.burst = 0;
        s.sprinting = false;
        if (d.yaw !== undefined) s.yaw = wrapAngle(d.yaw);
        s.vel.x = d.x;
        s.vel.z = d.z;
        s.vel.y = 0;
        const still = Math.abs(d.x) < 1e-6 && Math.abs(d.z) < 1e-6;
        return { x: d.x * dt, y: still ? -t.skin : 0, z: d.z * dt };
      }
      const on = moving(c);
      const mag = Math.min(1, Math.hypot(input.move.x, input.move.z));
      if (input.sprint && !s.sprinting && on) s.burst = t.burstTime;
      s.sprinting = input.sprint;
      s.burst = Math.max(0, s.burst - dt);
      const hs = groundSpeed(s);
      const ease = (k: number): number => 1 - Math.exp(-k * dt);
      if (!on) {
        s.pivot = false;
        const k = ease(r.decel);
        s.vel.x -= s.vel.x * k;
        s.vel.z -= s.vel.z * k;
      } else {
        const wish = yawOf(input.move.x, input.move.z);
        const offFacing = Math.abs(wrapAngle(wish - s.yaw));
        const skid = hs > t.pivotSpeed && Math.abs(wrapAngle(wish - yawOf(s.vel.x, s.vel.z))) > r.skidAngle;
        if (!skid && !s.pivot && hs < t.pivotSpeed && offFacing > r.pivotAngle) s.pivot = true;
        if (s.pivot && (offFacing < r.pivotRelease || skid)) s.pivot = false;
        if (skid) {
          const k = ease(t.skidDecel);
          s.vel.x -= s.vel.x * k;
          s.vel.z -= s.vel.z * k;
        } else if (s.pivot) {
          s.yaw = turnToward(s.yaw, wish, r.pivotRate * dt);
          const k = ease(r.decel);
          s.vel.x -= s.vel.x * k;
          s.vel.z -= s.vel.z * k;
        } else {
          s.yaw = turnToward(s.yaw, wish, r.turn * dt);
          const sprintTarget = s.burst > 0 ? t.sprintSpeed * (1 + t.burstBoost * Math.sin((Math.PI * (t.burstTime - s.burst)) / t.burstTime)) : t.sprintSpeed;
          const target = (input.sprint ? sprintTarget : speedForTilt(t, mag, false)) * c.speedScale;
          const k = s.burst > 0 && target > hs ? t.burstAccel : target > hs ? r.accel : r.decel;
          const speed = hs + (target - hs) * ease(k);
          const f = forwardOf(s.yaw);
          s.vel.x = f.x * speed;
          s.vel.z = f.z * speed;
        }
      }
      s.vel.y = 0;
      // standing still, a zero move tells Rapier nothing about the ground (it reads as not grounded after a teleport),
      // so ask for a nudge down a skin deep; moving, ask for a level move (a downward push while moving stalls it)
      const still = Math.abs(s.vel.x) < 1e-6 && Math.abs(s.vel.z) < 1e-6;
      return { x: s.vel.x * dt, y: still ? -t.skin : 0, z: s.vel.z * dt };
    },
  };
}

/**
 * Air: constant-gravity steps (exact, so the apex is the tuning's), heavier on the way down and while rising with the
 * jump let go (variable height); coyote time after walking off a ledge; a press too late for that is kept for the jump
 * buffer; capped air control that keeps momentum.
 */
export function airMode(): MovementMode {
  let dy = 0;
  return {
    id: "air",
    enter(c, from) {
      const s = c.s;
      if (from === "ground" && (c.input.jumpPressed || s.jumpQueued)) takeOff(c);
      else if (from === "ground") {
        s.vel.y = 0;
        s.jumped = false;
      }
    },
    step(c) {
      const { s, t, r, dt, input } = c;
      s.sinceGrounded += dt;
      if (input.jumpPressed && !c.jumpSpent) {
        // the planted defect sits here, beside the coyote jump it breaks
        if (!s.jumped && s.sinceGrounded <= t.coyoteTime + 1e-9 && !c.faults.noCoyote) takeOff(c);
        else s.buffer = 0;
      }
      s.jumpHeld = s.jumpHeld && input.jump;
      // horizontal: the stick pulls the velocity toward its direction at no more than airAccel, keeping momentum
      const hs = groundSpeed(s);
      const mag = Math.min(1, Math.hypot(input.move.x, input.move.z));
      if (moving(c)) {
        const wish = yawOf(input.move.x, input.move.z);
        s.yaw = turnToward(s.yaw, wish, r.airTurn * dt);
        const f = forwardOf(wish);
        const speed = Math.max(hs, speedForTilt(t, mag, s.sprinting));
        stepToward(s, f.x * speed, f.z * speed, t.airAccel * dt);
      } else if (hs > 0) {
        const k = Math.max(0, hs - t.airDrag * dt) / hs;
        s.vel.x *= k;
        s.vel.z *= k;
      }
      const g = r.gravity * (s.vel.y < 0 ? t.fallGravityScale : s.jumped && !s.jumpHeld ? t.jumpCutGravityScale : 1);
      dy = s.vel.y * dt - 0.5 * g * dt * dt;
      s.vel.y = Math.max(-t.maxFallSpeed, s.vel.y - g * dt);
      return { x: s.vel.x * dt, y: dy, z: s.vel.z * dt };
    },
    after(c) {
      // a ceiling stops the rise
      if (c.move && c.s.vel.y > 0 && c.move.moved.y < dy - 1e-4) c.s.vel.y = 0;
    },
  };
}

/** Glide: the Wanderer's scarf-carried glide. A fall is braked to a slow, steady sink; the stick steers and carries
 *  it forward at glide speed; with no stick it drifts on at its idle speed. */
export function glideMode(): MovementMode {
  return {
    id: "glide",
    enter(c) {
      c.s.jumped = true; // no coyote jump out of a glide
      c.s.jumpHeld = false;
    },
    step(c) {
      const { s, t, r, dt, input } = c;
      s.sinceGrounded += dt;
      if (input.jumpPressed) s.buffer = 0;
      const mag = Math.min(1, Math.hypot(input.move.x, input.move.z));
      if (moving(c)) s.yaw = turnToward(s.yaw, yawOf(input.move.x, input.move.z), r.glideTurn * dt);
      const f = forwardOf(s.yaw);
      // the game's per-step scales (a longer scarf: a slower sink, a faster glide)
      const gs = input.scale?.glideSpeed ?? 1;
      const speed = (moving(c) ? t.glideSpeed * mag : t.glideIdleSpeed) * gs;
      stepToward(s, f.x * speed, f.z * speed, t.glideAccel * dt);
      s.vel.y += (-t.glideSinkSpeed * (input.scale?.glideSink ?? 1) - s.vel.y) * (1 - Math.exp(-t.glideBrake * dt));
      return { x: s.vel.x * dt, y: s.vel.y * dt, z: s.vel.z * dt };
    },
  };
}

/** Move the horizontal velocity toward (x, z) by at most `max`. */
function stepToward(s: ControllerState, x: number, z: number, max: number): void {
  const dx = x - s.vel.x;
  const dz = z - s.vel.z;
  const d = Math.hypot(dx, dz);
  const k = d > max ? max / d : 1;
  s.vel.x += dx * k;
  s.vel.z += dz * k;
}

/** The built-in entry and exit rules, in the order they are checked. Rules to modes the game hasn't registered never
 *  fire (the slots). */
export function defaultRules(): ModeRule[] {
  return [
    // control handoff: a rider follows its mount, and comes back to its feet when it's given back
    { name: "mount", from: "*", to: "mounted", when: "input", test: (c) => c.s.riding !== null && c.s.mode !== "mounted" },
    { name: "dismount", from: "mounted", to: "ground", when: "input", test: (c) => c.s.riding === null },
    { name: "jump", from: "ground", to: "air", when: "input", test: (c) => c.input.jumpPressed || c.s.jumpQueued },
    { name: "glide-open", from: "air", to: "glide", when: "input", test: (c) => c.input.glidePressed && c.s.water === null && c.heightAbove(c.t.glideMinHeight) >= c.t.glideMinHeight },
    { name: "glide-close", from: "glide", to: "air", when: "input", test: (c) => !c.input.glide },
    { name: "fall", from: "ground", to: "air", when: "contact", test: (c) => c.move !== null && !c.move.grounded },
    { name: "land", from: "air", to: "ground", when: "contact", test: (c) => c.move !== null && c.move.grounded && c.s.vel.y <= 0 },
    { name: "glide-land", from: "glide", to: "ground", when: "contact", test: (c) => c.move !== null && c.move.grounded },
    { name: "glide-water", from: "glide", to: "air", when: "contact", test: (c) => c.s.water !== null },
    // slots
    { name: "swim", from: ["ground", "air", "glide"], to: "swim", when: "contact", test: (c) => c.s.water !== null && c.s.water.depth >= c.t.swimDepth },
    { name: "swim-out", from: "swim", to: "ground", when: "contact", test: (c) => c.s.water === null || c.s.water.depth < c.t.swimDepth * 0.8 },
    {
      name: "climb",
      from: ["ground", "air"],
      to: "climb",
      when: "contact",
      test: (c) =>
        c.move !== null &&
        moving(c) &&
        c.move.contacts.some((k) => k.tag !== null && c.climbTags.includes(k.tag) && Math.abs(k.normal.y) < 0.3 && k.normal.x * c.input.move.x + k.normal.z * c.input.move.z < -0.5 * Math.hypot(c.input.move.x, c.input.move.z)),
    },
  ];
}

/** Whether a rule leaves `mode`. */
export function ruleLeaves(rule: ModeRule, mode: ModeId): boolean {
  return rule.from === "*" || rule.from === mode || (Array.isArray(rule.from) && rule.from.includes(mode));
}
